import { useState, useEffect } from 'react';
import {
  Bot,
  Plus,
  Trash2,
  ChevronUp,
  ChevronDown,
  RefreshCw,
  Save,
  ExternalLink,
  AlertTriangle,
  Copy,
  Check
} from 'lucide-react';
import { adminAiConfigAPI } from '../../services/api';
import { useToast } from '../../context/ToastContext';

export default function AiConfigTab() {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [models, setModels] = useState([]);
  const [docsUrl, setDocsUrl] = useState('');
  const [adminNote, setAdminNote] = useState('');
  const [promptCopied, setPromptCopied] = useState(false);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const { data } = await adminAiConfigAPI.getConfig();
      const cfg = data.data;
      setModels(cfg.models || []);
      setDocsUrl(cfg.docsUrl || '');
      setAdminNote(cfg.note || '');
    } catch (error) {
      showToast('Failed to load AI configuration', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    // Remove empty model names
    const cleanModels = models.map(m => m.trim()).filter(Boolean);
    if (cleanModels.length === 0) {
      showToast('At least one model is required', 'error');
      return;
    }
    try {
      setSaving(true);
      await adminAiConfigAPI.updateConfig({
        models: cleanModels,
        docsUrl: docsUrl.trim(),
        note: adminNote.trim(),
      });
      setModels(cleanModels);
      showToast('AI configuration saved successfully', 'success');
    } catch (error) {
      showToast(error.response?.data?.error || 'Failed to save AI configuration', 'error');
    } finally {
      setSaving(false);
    }
  };

  const addModel = () => {
    setModels(prev => [...prev, '']);
  };

  const removeModel = (index) => {
    setModels(prev => prev.filter((_, i) => i !== index));
  };

  const updateModel = (index, value) => {
    setModels(prev => prev.map((m, i) => (i === index ? value : m)));
  };

  const moveModel = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= models.length) return;
    setModels(prev => {
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  // Ready-to-paste prompt for Gemini / AI Studio to find current model IDs
  // when one gets deprecated. Includes the currently configured models.
  const modelCheckPrompt = `I maintain the Gemini model fallback list for a Flutter app. It calls the Gemini API through the "google_generative_ai" Dart SDK version 0.4.7, which uses the CLASSIC function-calling protocol (it sends Content with role "function" and FunctionResponse parts, and it does NOT send a thought_signature). The app needs models that support: text chat, image/PDF vision, JSON structured output (responseSchema), AND function calling that works with that classic protocol.

Currently configured, in order of preference: ${models.map(m => m.trim()).filter(Boolean).join(', ') || '(none set)'}.

One or more of these just failed with errors like "Role 'function' is not supported" or "Function call is missing a thought_signature" or "no longer available". Those errors mean the model expects the NEWER function-calling protocol that the 0.4.7 SDK cannot produce.

For each model above, tell me if it is deprecated/retired, renamed, OR incompatible with the classic function-calling protocol. Then give me currently generally-available Gemini model IDs that DO work with this old SDK's classic function calling (these are typically the Gemini 2.x flash/pro models, NOT 3.x).

Answer with ONLY a comma-separated list of exact model IDs for my fallback chain, best/newest-compatible first. No explanation.`;

  const copyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(modelCheckPrompt);
      setPromptCopied(true);
      setTimeout(() => setPromptCopied(false), 1800);
      showToast('Prompt copied — paste it into Gemini', 'success');
    } catch {
      showToast('Could not copy prompt', 'error');
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-surface-200 p-8">
        <div className="flex items-center justify-center">
          <RefreshCw className="w-6 h-6 animate-spin text-surface-400" />
          <span className="ml-2 text-surface-500">Loading AI configuration...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl border border-surface-200 p-6">
        <div className="flex items-center gap-3 mb-2">
          <Bot className="w-6 h-6 text-primary-600" />
          <h2 className="text-lg font-semibold text-surface-900">AI Model Configuration</h2>
        </div>
        <p className="text-sm text-surface-500">
          Manage Gemini model fallback chain. When a model fails, the next one is tried.
          Update here when Google deprecates a model — no app update needed.
        </p>
      </div>

      {/* Model Fallback Chain */}
      <div className="bg-white rounded-2xl border border-surface-200 p-6">
        <h3 className="text-base font-semibold text-surface-900 mb-1">Model Fallback Chain</h3>
        <p className="text-sm text-surface-500 mb-4">
          Models are tried in order (top → bottom). First successful response wins. The app fetches this list on every AI request.
        </p>

        {models.length === 0 && (
          <div className="flex items-center gap-2 p-4 bg-amber-50 border border-amber-200 rounded-xl mb-4">
            <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0" />
            <p className="text-sm text-amber-700">No models configured. AI features will not work until at least one model is added.</p>
          </div>
        )}

        <div className="space-y-3">
          {models.map((model, index) => (
            <div key={index} className="flex items-center gap-3">
              {/* Position badge */}
              <span className="flex-shrink-0 w-7 h-7 rounded-full bg-primary-600/10 text-primary-600 text-xs font-bold flex items-center justify-center">
                {index + 1}
              </span>

              {/* Model name input */}
              <input
                type="text"
                value={model}
                onChange={(e) => updateModel(index, e.target.value)}
                placeholder="e.g. gemini-2.5-flash"
                className="flex-1 px-3 py-2 border border-surface-200 rounded-lg text-sm text-surface-900 placeholder-surface-400 focus:outline-none focus:ring-2 focus:ring-primary-600/30 focus:border-primary-600"
              />

              {/* Move up */}
              <button
                onClick={() => moveModel(index, -1)}
                disabled={index === 0}
                className="p-1.5 rounded-lg hover:bg-surface-100 text-surface-400 hover:text-surface-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                title="Move up"
              >
                <ChevronUp className="w-4 h-4" />
              </button>

              {/* Move down */}
              <button
                onClick={() => moveModel(index, 1)}
                disabled={index === models.length - 1}
                className="p-1.5 rounded-lg hover:bg-surface-100 text-surface-400 hover:text-surface-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                title="Move down"
              >
                <ChevronDown className="w-4 h-4" />
              </button>

              {/* Delete */}
              <button
                onClick={() => removeModel(index)}
                className="p-1.5 rounded-lg hover:bg-red-50 text-surface-400 hover:text-red-500 transition-colors"
                title="Remove model"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>

        <button
          onClick={addModel}
          className="mt-4 flex items-center gap-2 px-4 py-2 text-sm font-medium text-primary-600 hover:bg-primary-600/5 border border-dashed border-primary-600/30 rounded-lg transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Model
        </button>
      </div>

      {/* Prompt to check current models */}
      <div className="bg-white rounded-2xl border border-surface-200 p-6">
        <div className="flex items-center justify-between gap-3 mb-1">
          <h3 className="text-base font-semibold text-surface-900">Not sure which model to use?</h3>
          <button
            onClick={copyPrompt}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors flex-shrink-0 ${
              promptCopied
                ? 'text-green-700 bg-green-100'
                : 'text-primary-600 bg-primary-600/10 hover:bg-primary-600/20'
            }`}
          >
            {promptCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            {promptCopied ? 'Copied' : 'Copy prompt'}
          </button>
        </div>
        <p className="text-sm text-surface-500 mb-3">
          When a model fails, copy this and paste it into Gemini (or Google AI Studio). It will tell you which models are deprecated and the current IDs to use, then paste those into the list above.
        </p>
        <pre className="text-xs text-surface-600 bg-surface-50 border border-surface-200 rounded-xl p-4 whitespace-pre-wrap font-mono max-h-56 overflow-y-auto">{modelCheckPrompt}</pre>
      </div>

      {/* Documentation & Notes */}
      <div className="bg-white rounded-2xl border border-surface-200 p-6">
        <h3 className="text-base font-semibold text-surface-900 mb-4">Documentation & Notes</h3>

        {/* Docs URL */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-surface-700 mb-1">
            Google AI Models Documentation URL
          </label>
          <div className="flex items-center gap-2">
            <input
              type="url"
              value={docsUrl}
              onChange={(e) => setDocsUrl(e.target.value)}
              placeholder="https://ai.google.dev/gemini-api/docs/models"
              className="flex-1 px-3 py-2 border border-surface-200 rounded-lg text-sm text-surface-900 placeholder-surface-400 focus:outline-none focus:ring-2 focus:ring-primary-600/30 focus:border-primary-600"
            />
            {docsUrl && (
              <a
                href={docsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-lg hover:bg-surface-100 text-primary-600 transition-colors"
                title="Open documentation"
              >
                <ExternalLink className="w-4 h-4" />
              </a>
            )}
          </div>
          <p className="text-xs text-surface-400 mt-1">
            Link to check available models when one gets deprecated
          </p>
        </div>

        {/* Admin Note */}
        <div>
          <label className="block text-sm font-medium text-surface-700 mb-1">
            Admin Note
          </label>
          <textarea
            value={adminNote}
            onChange={(e) => setAdminNote(e.target.value)}
            placeholder="e.g. gemini-2.0-flash retired Jun 2026. gemini-1.5-flash retired Sep 2025."
            rows={4}
            className="w-full px-3 py-2 border border-surface-200 rounded-lg text-sm text-surface-900 placeholder-surface-400 focus:outline-none focus:ring-2 focus:ring-primary-600/30 focus:border-primary-600 resize-none"
          />
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-2.5 bg-primary-600 text-white rounded-xl font-medium text-sm hover:bg-primary-600/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {saving ? (
            <RefreshCw className="w-4 h-4 animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          {saving ? 'Saving...' : 'Save Configuration'}
        </button>
      </div>
    </div>
  );
}
